import { GlassCard } from '@/components/design-system';
import { EmptyState } from '@/components/poof-ui';
import { cn } from '@/lib/utils';
import type { ListingsResponse } from '@/lib/collections/listings';
import { formatCentsAsUsd } from '@/utils/plastic-rates';
import { Copy, ExternalLink, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';

function shortSig(sig: string): string {
  return sig.length > 10 ? `${sig.slice(0, 5)}…${sig.slice(-5)}` : sig;
}

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface EarningsHistoryCardProps {
  /** Completed listings belonging to the connected household, newest first. */
  listings: ListingsResponse[];
}

/**
 * Real transaction history of payments the household received — one row per
 * completed listing with a settled tx signature. Reuses the copy + Solscan
 * link pattern and amber "paid" visual language from ListingTimelineCard /
 * the company Complete & Pay flow for consistency across the app.
 */
export function EarningsHistoryCard({ listings }: EarningsHistoryCardProps) {
  const rows = listings.filter((listing) => !!listing.txSignature);

  const copySig = async (sig: string) => {
    try {
      await navigator.clipboard.writeText(sig);
      toast.success('Transaction signature copied');
    } catch {
      toast.error('Could not copy.');
    }
  };

  if (rows.length === 0) {
    return (
      <GlassCard padding="lg" className="h-full">
        <EmptyState
          icon={<PiggyBank className="h-6 w-6" />}
          title="No earnings yet"
          description="Once a company completes and pays for one of your listings, it'll show up here with the on-chain receipt."
        />
      </GlassCard>
    );
  }

  return (
    <GlassCard padding="lg" className="h-full">
      <span className="text-overline">Earnings</span>
      <h2 className="font-heading mt-1 text-xl font-bold text-foreground">Transaction history</h2>

      <div className="mt-4 max-h-[22rem] space-y-2 overflow-y-auto pr-1">
        {rows.map((listing) => (
          <div
            key={listing.id}
            className="flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--amber))]/25 bg-[hsl(var(--amber))]/[0.06] px-3.5 py-3"
          >
            <div>
              <p className="text-sm font-semibold text-foreground">
                {formatCentsAsUsd(listing.estimatedValue)} <span className="text-muted-foreground">USDC</span>
              </p>
              <p className="text-xs text-muted-foreground">{formatDate(listing.completedAt ?? 0)}</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => copySig(listing.txSignature as string)}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                title="Copy signature"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
              <a
                href={`https://solscan.io/tx/${listing.txSignature}`}
                target="_blank"
                rel="noopener noreferrer"
                className={cn('flex items-center gap-1 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground')}
                title="View on Solscan"
              >
                <span className="font-mono text-[11px]">{shortSig(listing.txSignature as string)}</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

export default EarningsHistoryCard;
